import { tokens } from '@neo4j-ndl/base';
import { Banner, Button, Dialog, Radio, TextLink, Tooltip, Typography } from '@neo4j-ndl/react';
import { ChevronDownIconOutline, InformationCircleIconOutline } from '@neo4j-ndl/react/icons';
import type { Connection } from '@nx/state';
import {
  MODAL_TYPE,
  StoreKit,
  authLog,
  ssoSigninRedirect,
  useCapability,
  useConnection,
  useExternalConnectionsFlag,
  useModal,
} from '@nx/state';
import { URLs, isNotNullish, isNullish } from '@nx/stdlib';
import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import {
  getFormValuesFromUrl,
  getProtocolOptions,
  getProtocols,
  validateForm,
} from '../connection-form/connection-form-utils';
import type { ConnectionFormValues } from '../connection-form/connection-form.types';
import ConnectionMessage from '../connection-form/form-elements/connection-error';
import { ConnectionFormBasicAuth } from '../connection-form/form-elements/input-basic-auth';
import { ConnectionFormSso } from '../connection-form/form-elements/input-sso';
import { ConnectionFormUrl } from '../connection-form/form-elements/input-url';
import { useConnect, useFormValues } from '../connection-form/hooks';
import { DividerWithLabel } from '../divider-with-label/divider-with-label';
import { useNeo4jDiscovery } from '../hooks/use-neo4j-discovery';
import type { ConnectionCallback, ConnectionNotification } from './external-connection-modal';
import { ExternalConnectionModal } from './external-connection-modal';
import { isFrontChannel } from './utils';

interface FormProps {
  formValues: ConnectionFormValues;
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement> | { target: { name: string; value: string } }) => void;
}

function ProtocolRadioButton({
  label,
  tipText,
  tipPlacement = 'bottom',
  value,
  isDisabled,
  formProps,
}: {
  label: string;
  tipText?: string;
  tipPlacement: 'top' | 'bottom';
  value: URLs.ConnectionProtocol;
  isDisabled?: boolean;
  formProps: FormProps;
}) {
  return (
    <span className="flex items-center">
      <Radio
        label={label}
        onChange={formProps.handleInputChange}
        isChecked={formProps.formValues.protocol === value}
        htmlAttributes={{
          value,
          name: 'protocol',
        }}
        isDisabled={isDisabled}
      />
      {isNotNullish(tipText) && (
        <Tooltip placement={tipPlacement} type="simple" isPortaled={false}>
          <Tooltip.Trigger hasButtonWrapper>
            <InformationCircleIconOutline className="ml-2 inline-block size-4" aria-label={`${label} explanation`} />
          </Tooltip.Trigger>
          <Tooltip.Content className="max-w-sm">{tipText}</Tooltip.Content>
        </Tooltip>
      )}
    </span>
  );
}

const ALLOWED_AURA_PROTOCOLS: URLs.ConnectionProtocol[] = ['neo4j+s:', 'https:'];

function ConnectionMethods({ formProps }: { formProps: FormProps }) {
  const { formValues } = formProps;
  const discovery = useNeo4jDiscovery(`${formValues.protocol}//${formValues.hostname}`);

  const [isOpen, setIsOpen] = useState(false);

  const [allOptionsAllowed] = useCapability('framework:allow-all-connection-form-fields');
  const protocols = getProtocols(formProps.formValues.hostname).filter(
    (protocol) => allOptionsAllowed || ALLOWED_AURA_PROTOCOLS.includes(protocol),
  );

  const protocolOptions = getProtocolOptions(protocols, discovery.data?.neo4jVersion);

  return (
    <div>
      <Button
        fill="text"
        className="text-palette-primary-text relative right-2 flex h-9 items-center gap-1.5 !px-2"
        aria-label="Connection method selection"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <Typography variant="body-medium">Connection method</Typography>
        <ChevronDownIconOutline
          className="text-palette-primary-text h-4 w-4 shrink-0"
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transitionDuration: tokens.transitions.values.duration.quick,
          }}
        />
      </Button>
      {isOpen && (
        <div className="mt-1 flex w-full flex-col gap-2">
          {protocolOptions.map((option, index) => (
            <ProtocolRadioButton
              key={option.friendlyLabel}
              label={option.friendlyLabel}
              tipText={option.description}
              tipPlacement={index === 0 ? 'top' : 'bottom'}
              value={option.value}
              formProps={formProps}
              isDisabled={option.isDisabled}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Sso({
  formProps,
  connectionSource = { type: 'user' },
  isTransient = true,
}: {
  formProps: FormProps;
  connectionSource?: Connection['source'];
  isTransient?: Connection['isTransient'];
}) {
  const { formValues } = formProps;
  const { providers } = useNeo4jDiscovery(`${formValues.protocol}//${formValues.hostname}`);

  if (providers.length <= 0) {
    return null;
  }

  const showFrontChannelWarning = providers.some(isFrontChannel);

  return (
    <div className="flex w-full flex-col gap-4">
      <h6>Single sign on</h6>
      {showFrontChannelWarning && (
        <Banner
          hasIcon
          type="warning"
          description={
            <div className="flex flex-col gap-2">
              <Typography variant="body-medium">
                If you have issues connecting with tools over SSO, your IDP redirect URIs may need to be updated. Please
                refer to this{' '}
                <TextLink href="https://support.neo4j.com/s/article/UPX-Domain-Switch">KB article</TextLink>.
              </Typography>
              <Typography variant="body-medium">
                To continue connecting with tools over SSO, you can revert back to the classic console experience via
                the user menu in the console's header.
              </Typography>
            </div>
          }
          className="mb-2 flex items-center"
        />
      )}
      <ConnectionFormSso
        connectionSource={connectionSource}
        isTransient={isTransient}
        formValues={formValues}
        providers={providers}
      />
      <DividerWithLabel label="OR" className="mt-1" />
    </div>
  );
}

function BasicAuth({ allowAnyUrl, formProps }: { allowAnyUrl?: boolean; formProps: FormProps }) {
  const [allOptionsAllowed] = useCapability('framework:allow-all-connection-form-fields');

  return (
    <div className="flex w-full flex-col gap-4">
      <h6>Database credentials</h6>
      {(allowAnyUrl === true || allOptionsAllowed) && (
        <ConnectionFormUrl formValues={formProps.formValues} handleInputChange={formProps.handleInputChange} />
      )}
      <ConnectionFormBasicAuth
        formValues={formProps.formValues}
        handleInputChange={formProps.handleInputChange}
        displayPasswordVisibilityToggle
      />
    </div>
  );
}

const emptyFormValues: ConnectionFormValues = {
  name: '',
  protocol: URLs.PROTOCOL_NEO4J_SECURE,
  hostname: '',
  username: '',
  password: '',
};

interface ConnectionModalProps {
  allowAliasChange?: boolean;
  connectionSource: Connection['source'];
  instanceDetails?: {
    id: string;
    name: string;
  };
  initialValues?: Partial<ConnectionFormValues>;
  initialWarning?: string;
  initialError?: true | string;
  hideConnectionMethods?: boolean;
}

function LegacyConnectionModal({
  connectionSource,
  hideConnectionMethods,
  instanceDetails,
  initialValues,
  message,
  onConnect,
}: ConnectionModalProps & { message?: ConnectionNotification; onClose: () => void; onConnect: ConnectionCallback }) {
  const [ssoEnabled] = useCapability('framework:sso-db-connection');
  const { close: closeModal } = useModal(MODAL_TYPE.CONNECTION_FORM);
  const connection = useConnection();
  const [formValues, setFormValues] = useFormValues({
    ...emptyFormValues,
    name: instanceDetails?.name ?? '',
    ...initialValues,
  });

  const { isValid } = validateForm(formValues);

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement> | { target: { name: string; value: string } },
  ) => {
    const { name, value } = event.target;
    setFormValues({ [name]: value }, { resetInstanceName: true });
  };

  const formProps = {
    formValues,
    handleInputChange,
  };

  return (
    <Dialog
      isOpen
      size="unset"
      modalProps={{ className: 'max-w-[480px] overflow-visible mx-auto mt-12 p-10 align-top' }}
      onClose={closeModal}
    >
      <Dialog.Header>
        {instanceDetails ? (
          <>
            Connect to <span className="inline font-normal">{instanceDetails.name}</span>
          </>
        ) : (
          'Connect to an instance'
        )}
      </Dialog.Header>
      <Dialog.Content>
        {message !== undefined && message.type === 'driver-error' && (
          <div className="mb-2">
            <ConnectionMessage
              show
              error={message.error}
              discoveryUrls={isNullish(instanceDetails) ? message.discoveryUrls : []}
              onDiscoveryUrlSelected={(url) => setFormValues(getFormValuesFromUrl(url))}
            />
          </div>
        )}
        {message !== undefined && (message.type === 'warning' || message.type === 'error') && (
          <Banner
            hasIcon
            type={message.type === 'error' ? 'danger' : message.type}
            description={<Typography variant="body-medium">{message.value}</Typography>}
            className="mb-2 flex items-center"
          />
        )}
        {isNullish(instanceDetails) || connectionSource.type !== 'aura' ? (
          <div className="w-full truncate pb-4">
            <Typography variant="body-medium">Connection URL</Typography>
            <Typography variant="body-medium" className="text-palette-neutral-text-weak">
              <br />
              {`${formValues.protocol}//${formValues.hostname}`}
            </Typography>
          </div>
        ) : null}
        <form
          className="flex flex-col items-start gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            const { protocol, hostname, ...values } = formValues;
            void onConnect({
              type: 'basic',
              inputs: {
                ...values,
                enableProjectSharing: false,
                url: `${protocol}//${hostname}`,
              },
            });
          }}
        >
          {isNullish(hideConnectionMethods) || !hideConnectionMethods ? (
            <ConnectionMethods formProps={formProps} />
          ) : null}
          {ssoEnabled ? (
            <Sso
              formProps={formProps}
              connectionSource={
                instanceDetails?.id !== undefined
                  ? {
                      type: 'aura',
                      sourceId: instanceDetails.id,
                    }
                  : { type: 'user' }
              }
            />
          ) : null}
          <BasicAuth allowAnyUrl={connectionSource.type !== 'aura'} formProps={formProps} />
          <Button
            type="submit"
            className="mt-3 w-full"
            isDisabled={connection.status.isConnecting || connection.status.isDisconnecting || !isValid}
          >
            Connect
          </Button>
        </form>
      </Dialog.Content>
    </Dialog>
  );
}

export function ConnectionModal(props: ConnectionModalProps) {
  const externalConnectionsEnabled = useExternalConnectionsFlag();
  const [isConnectionError, setIsConnectionError] = useState(props.initialError === true);

  const location = useLocation();
  const thisModal = useModal(MODAL_TYPE.CONNECTION_FORM);
  const connection = useConnection();
  const { connectBasicAuth } = useConnect();

  const message = useMemo((): ConnectionNotification | undefined => {
    if (props.initialError === true || isConnectionError) {
      return {
        type: 'driver-error',
        error: connection.metadata?.error,
        discoveryUrls: connection.metadata?.discoveryUrls,
      };
    }

    if (props.initialError !== undefined) {
      return {
        type: 'error',
        value: props.initialError,
      };
    }

    if (props.initialWarning !== undefined) {
      return {
        type: 'warning',
        value: props.initialWarning,
      };
    }

    return undefined;
  }, [connection.metadata, isConnectionError, props.initialError, props.initialWarning]);

  function handleSubmit(formValues: ConnectionFormValues) {
    connectBasicAuth({ formValues, onError: () => setIsConnectionError(true), source: props.connectionSource });
  }

  const handleConnect: ConnectionCallback = async (payload, event) => {
    if (event !== undefined) {
      event.preventDefault();
    }

    if (payload.type === 'sso') {
      const { globalPersistor } = StoreKit.UNSAFE_getGlobalStoreInstance();

      connection.provisionSso({ url: payload.inputs.url, source: props.connectionSource });

      // Here we're ensuring that the connection data is saved to the store by calling flush before we redirect
      // to the SSO login page by calling ssoSigninRedirect, otherwise the connection will fail.
      await globalPersistor.flush();

      // The modal is closed here so ssoSigninRedirect will persist the background
      // URL instead of the URL to the connection form, before redirecting the user.
      thisModal.close();

      // This is where we redirect the user.
      ssoSigninRedirect(payload.provider, location.pathname, location.search).catch((error: Error) => {
        authLog(error.message, 'error');
      });
    } else {
      const neo4jUrl = new URLs.Neo4jURL(payload.inputs.url);
      handleSubmit({
        protocol: neo4jUrl.protocol,
        hostname: neo4jUrl.host,
        name: payload.inputs.name,
        username: payload.inputs.username,
        password: payload.inputs.password,
      });
    }
  };

  if (externalConnectionsEnabled) {
    const url =
      props.initialValues?.hostname !== undefined
        ? `${props.initialValues.protocol}//${props.initialValues.hostname}`
        : '';
    return (
      <ExternalConnectionModal
        allowAliasChange={props.allowAliasChange}
        allowPasswordReveal
        allowProtocolChange={props.connectionSource.type === 'aura'}
        allowUrlChange={props.connectionSource.type !== 'aura' && url === ''}
        defaultValues={{
          name: props.instanceDetails?.name,
          username: props.initialValues?.username ?? 'neo4j',
          url,
        }}
        message={message}
        onClose={() => {
          thisModal.close();
        }}
        onConnect={handleConnect}
      />
    );
  }

  return (
    <LegacyConnectionModal {...props} message={message} onClose={() => thisModal.close()} onConnect={handleConnect} />
  );
}
